import { ClaimStatusRefreshError, ClaimStatusUnreconciledItem } from '~support-at-home/models';

/**
 * Claim-status sweep findings, made readable (TAV-2596).
 *
 * The sweep's `errors[]` carries two unrelated shapes. Sweep FAILURES are `{ sa_claim_id?, message }`
 * and are already covered: a rejected refresh toasts through `parseSahApiError`. The other shape is a
 * FINDING rather than a failure - a payment item on a statement Services Australia paid that did not
 * match any claim item, which is AC #7's "unmatched items surfaced for provider review".
 *
 * That distinction is why this cannot key off `message`: an unreconciled entry has no `message` field
 * at all, so rendering the array as failures produced blank lines.
 *
 * Only `UNMATCHED` is surfaced. `SKIPPED` means the item was already reconciled, which is a normal
 * outcome on a re-pull and needs no provider action; it stays in the payload (and in the API's durable
 * copy) so the distinction survives, but it is not worth a toast.
 */

// Enough refs to be actionable without turning a toast into a wall of identifiers.
const MAX_LISTED_REFS = 3;

function isUnreconciledItem(entry: ClaimStatusRefreshError): entry is ClaimStatusUnreconciledItem {
  return 'reason' in entry;
}

export function selectUnmatchedItems(errors: ClaimStatusRefreshError[] = []): ClaimStatusUnreconciledItem[] {
  return errors.filter(isUnreconciledItem).filter((entry) => entry.reason === 'UNMATCHED');
}

/**
 * A one-line summary of the unmatched items, or null when there is nothing to say. Null rather than
 * an empty string so the caller's guard is a single truthiness check and can never toast a blank.
 */
export function summariseUnmatchedItems(errors: ClaimStatusRefreshError[] = []): string | null {
  const unmatched = selectUnmatchedItems(errors);
  if (unmatched.length === 0) return null;

  // `ref` is the provider-facing identifier (the supplier invoice item reference). It is nullable on
  // the API side, so a statement item with no reference contributes to the count but cannot be named.
  const refs = unmatched.map((entry) => entry.ref).filter((ref): ref is string => Boolean(ref));
  const listed = refs.slice(0, MAX_LISTED_REFS).join(', ');
  const remainder = refs.length - Math.min(refs.length, MAX_LISTED_REFS);

  const noun = unmatched.length === 1 ? 'payment item' : 'payment items';
  const headline = `Services Australia paid ${unmatched.length} ${noun} that could not be matched to a claim item.`;
  if (!listed) return `${headline} Review the claim to see which.`;

  const tail = remainder > 0 ? `${listed} and ${remainder} more` : listed;
  return `${headline} Needs review: ${tail}.`;
}
